/**
 * Main Application Initialization Module
 * Entry point that coordinates all other modules
 */

// Check if debug mode is enabled
// debugMode может быть определен в params.js (defer), поэтому проверяем window.debugMode
const urlParams = new URLSearchParams(window.location.search);
const debugMode = window.debugMode || urlParams.get('debug') || '0';

if (debugMode === '1') {
}

// Import core modules
import { unzip } from './storage/local-storage.js';
import { initializeSearchOrchestrator as initializePhase10Module } from './app/search-orchestrator.js';
import { connectCoreModules } from './app/connect-modules.js';
import { loadInitialData as loadInitialDataModule } from './app/load-data.js';
import { searchEngine } from './search/search-engine.js';
import { filterManager } from './filters/filter-manager.js';
import { fileListManager } from './files/file-list.js';
import { filesCacheManager } from './storage/files-cache.js';
import { searchUI } from './ui/search-ui.js';
import { paginationManager } from './ui/pagination.js';
import './storage/files-store.js';
import '../modules/filters/date-range-litepicker-utils.js';
import '../modules/filters/date-range-litepicker-ui.js';
import '../modules/filters/date-range-litepicker-core.js';
import '../modules/filters/date-range-litepicker.js';

// Import Phase 3 modules - Search Logic
import { getMatchConditions, myEach, addCategoryMatchConditions } from './search/search-conditions.js';
import { substringMatcher } from './search/search-coordinator.js';

// Import Phase 4 modules - File Management
import { fileLoader } from './files/file-loader.js';
import { fileProcessor } from './files/file-processor.js';

// Import Phase 5 modules - UI Components
import { searchProgressManager } from './ui/search-progress.js';
import { paginationController } from './ui/pagination-controller.js';

// Import Phase 6 modules - Filters and Forms
import { filterController } from './filters/filter-controller.js';
import { dateFiltersManager } from './filters/date-filters.js';
import { formHandler } from './forms/form-handler.js';

// Import Phase 7 modules - Card Management
import { cardController } from './cards/card-controller.js';
import { cardEventsManager } from './cards/card-events.js';

// Import Phase 8 modules - App Initialization & Bootstrap
import { appInitializationManager } from './app/app-initialization.js';
import { bootstrapManager } from './ui/bootstrap-manager.js';
import { tabManager } from './ui/tab-manager/index.js';

// Import Phase 9 modules - Date & Time Management
import { dateParser } from './datetime/date-parser.js';
import { dateFormatter } from './datetime/date-formatter.js';
import { dateValidator } from './datetime/date-validator.js';
import { timezoneManager } from './datetime/timezone-manager.js';

// Import Phase 11 modules - Debug & Diagnostics
import { generateFilterReport, testFilterFunctionality, validateDataIntegrity, simulateFilterApplication } from './debug/filter-diagnostics.js';

// Import refactored modules
import { setupGlobalExports, checkLegacyCompatibility } from './main-init/exports.js';
import { AppConfig } from './main-init/config.js';
import { initializeModules } from './main-init/modules-initializer.js';
import { processFileData, extractFormat, getFileIcon, getFileItemTemplate, getFilterElements } from './main-init/file-utils.js';
import { LoaderManager } from './main-init/loader.js';
import { showError, showKeyboardShortcuts, closeAllModals, trackEvent, broadcastThemeChange, handleInitError } from './main-init/utils.js';

// Import delegated modules
import { setupEventListeners as setupEventListenersModule } from './app/events.js';
import { updateStatistics as updateStatisticsModule, formatFileSize as formatFileSizeModule, updateFormatBreakdown as updateFormatBreakdownModule, updateCategoryBreakdown as updateCategoryBreakdownModule } from './app/statistics.js';
import { languageManager } from './lang/language-manager.js';

// Setup global exports and legacy compatibility
setupGlobalExports({
    getMatchConditions,
    myEach,
    addCategoryMatchConditions,
    substringMatcher,
    generateFilterReport,
    testFilterFunctionality,
    validateDataIntegrity,
    simulateFilterApplication,
    fileLoader,
    fileProcessor,
    filesCacheManager,
    searchProgressManager,
    paginationController,
    filterController,
    dateFiltersManager,
    formHandler,
    cardController,
    cardEventsManager,
    appInitializationManager,
    bootstrapManager,
    tabManager,
    dateParser,
    dateFormatter,
    dateValidator,
    timezoneManager
});

checkLegacyCompatibility(debugMode === '1' ? '1' : '0');

export class MainApp {
    constructor() {
        if (debugMode === '1') {
        }

        this.configManager = new AppConfig();
        this.config = this.configManager.config;
        this.modules = {};
        this.state = {
            initialized: false,
            loading: false,
            error: null
        };
        this.lastFilesTimestamp = null;
        this.appReady = false;
        this.pageLoaded = (document.readyState === 'complete');
        this.loaderManager = new LoaderManager();

        try {
            window.addEventListener('load', () => {
                this.pageLoaded = true;
                this.loaderManager.attemptHideCriticalLoader(this.appReady, this.pageLoaded, debugMode === '1' ? '1' : '0');
            }, { once: true });
            this.loaderManager.setupSafetyTimeout(debugMode === '1' ? '1' : '0');
        } catch(e) {
            console.error('[main-init] ❌ Failed to setup load event listener:', e);
        }

        if (debugMode === '1') {
        }
    }
    
    // Initialize application
    async init() {
        try {
            if (debugMode === '1') {
            }
            
            // Migration completed - skip migration controller
            if (debugMode === '1') {
            }
            
            // Ensure DOM is ready
            if (document.readyState === 'loading') {
                await new Promise(resolve => {
                    document.addEventListener('DOMContentLoaded', resolve);
                });
            }
            
            // Initialize migration infrastructure
            await this.initializeMigrationInfrastructure();
            
            // Initialize all modules
            await this.initializeModules();
            
            // Load initial data
            await this.loadInitialData();
            
            // Mark as ready
            this.markAsReady();
            
            if (debugMode === '1') {
            }
        } catch (error) {
            console.error('[main-init] ❌ Application initialization failed:', error);
            if (debugMode === '1') {
                console.error('[main-init] Application initialization failed:', error);
            }
            this.handleInitError(error);
        }
    }

    // Initialize migration infrastructure
    async initializeMigrationInfrastructure() {
        if (debugMode === '1') {
        }
    }

    // Load application configuration
    async loadConfiguration() {
        await this.configManager.loadConfiguration();
        this.config = this.configManager.config;
    }
    
    // Initialize all modules
    async initializeModules() {
        return initializeModules(this, {
            initializePhase10: () => this.initializePhase10(),
            connectModules: () => this.connectModules(),
            config: this.config,
            debugMode: debugMode === '1' ? '1' : '0'
        });
    }

    // Initialize Phase 10: Advanced Search & Fuzzy Logic
    async initializePhase10() {
        return initializePhase10Module({ debug: (debugMode === '1') });
    }
    
    // Connect modules together
    connectModules() {
        return connectCoreModules({
            app: this,
            filterManager,
            searchEngine,
            fileListManager,
            paginationManager,
            searchUI,
            filesCacheManager,
            languageManager,
            debug: (debugMode === '1')
        });
    }

    // File utilities
    getFilterElements() { return getFilterElements(); }
    getFileItemTemplate() { return getFileItemTemplate(); }
    processFileData(rawData) { return processFileData(rawData); }
    extractFormat(filename) { return extractFormat(filename); }
    getFileIcon(format) { return getFileIcon(format); }

    setupEventListeners() { return setupEventListenersModule(this); }
    handleKeyboardShortcut(event) { /* delegated to app/events.js */ }
    async handleFileDownload(fileId) { /* delegated to app/events.js */ }
    handleResize() { /* delegated to app/events.js */ }
    toggleMobileMenu() { /* delegated to app/events.js */ }
    toggleTheme() { /* delegated to app/events.js */ }

    async loadInitialData() {
        return loadInitialDataModule(this, { filesCacheManager, searchEngine, fileListManager, unzip });
    }

    updateStatistics(files) { return updateStatisticsModule(this, files); }
    formatFileSize(bytes) { return formatFileSizeModule(bytes); }
    updateFormatBreakdown(formatStats) { return updateFormatBreakdownModule(this, formatStats); }
    updateCategoryBreakdown(categoryStats) { return updateCategoryBreakdownModule(this, categoryStats); }

    // Loader methods
    showLoading(show) { this.loaderManager.showLoading(show); }
    updateLoaderProgress(percent, label) { this.loaderManager.updateLoaderProgress(percent, label); }
    toggleLoaderProgress(show) { this.loaderManager.toggleLoaderProgress(show); }
    attemptHideCriticalLoader() {
        try {
            this.loaderManager.attemptHideCriticalLoader(this.appReady, this.pageLoaded, debugMode === '1' ? '1' : '0');
        } catch (error) {
            console.error('[main-init] ❌ attemptHideCriticalLoader() failed:', error);
        }
    }

    // Utility methods
    showError(message) { showError(message, debugMode === '1' ? '1' : '0'); }
    showKeyboardShortcuts() { showKeyboardShortcuts(debugMode === '1' ? '1' : '0'); }
    closeAllModals() { closeAllModals(); }
    trackEvent(eventName, data) { trackEvent(eventName, data, debugMode === '1' ? '1' : '0'); }
    broadcastThemeChange(theme) { broadcastThemeChange(this.modules, theme); }
    
    // Initialize application
    async init() {
        try {
            if (debugMode === '1') {
            }
            
            // ✅ CRITICAL: Register component status with weight (percentage)
            if(window.ComponentLoadingStatus){
                let appInitCaption = 'Initializing application...';
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                    try {
                        const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                        const translated = window.TranslationRegistry.translate('loader_stage_initializing_application', lang);
                        if (translated !== 'loader_stage_initializing_application') {
                            appInitCaption = translated;
                        }
                    } catch(_) {}
                }
                window.ComponentLoadingStatus.register('mainApp', 'loading', appInitCaption, 8);
            }
            
            // Migration completed - skip migration controller
            if (debugMode === '1') {
            }
            
            // Ensure DOM is ready
            if (document.readyState === 'loading') {
                await new Promise(resolve => {
                    document.addEventListener('DOMContentLoaded', resolve);
                });
            }
            
            // Initialize migration infrastructure
            await this.initializeMigrationInfrastructure();
            
            // Initialize all modules
            await this.initializeModules();
            
            // Load initial data
            await this.loadInitialData();
            
            // Mark as ready
            this.markAsReady();
            
            if (debugMode === '1') {
            }
        } catch (error) {
            if (debugMode === '1') {
                console.error('[main-init] Application initialization failed:', error);
            }
            // ✅ CRITICAL: Update component status to 'error' on failure
            if(window.ComponentLoadingStatus){
                let appFailedCaption = 'Application initialization failed';
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                    try {
                        const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                        const translated = window.TranslationRegistry.translate('loader_stage_application_init_failed', lang);
                        if (translated !== 'loader_stage_application_init_failed') {
                            appFailedCaption = translated;
                        }
                    } catch(_) {}
                }
                window.ComponentLoadingStatus.update('mainApp', 'error', appFailedCaption);
            }
            this.handleInitError(error);
        }
    }
    
    // Mark application as ready
    markAsReady() {
        this.state.initialized = true;
        this.state.loading = false;
        this.state.error = null;
        this.appReady = true;

        if (debugMode === '1') {
        }

        // ✅ CRITICAL: Update component status to 'ready'
        if(window.ComponentLoadingStatus){
            let appReadyCaption = 'Application ready';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                try {
                    const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                    const translated = window.TranslationRegistry.translate('loader_stage_application_ready', lang);
                    if (translated !== 'loader_stage_application_ready') {
                        appReadyCaption = translated;
                    }
                } catch(_) {}
            }
            window.ComponentLoadingStatus.update('mainApp', 'ready', appReadyCaption);
        }

        const readyEvent = new CustomEvent('mainAppReady', {
            detail: {
                modules: this.modules,
                state: this.state,
                timestamp: Date.now()
            }
        });
        window.dispatchEvent(readyEvent); // ✅ Use window for global event (like audio lectures pattern)
        document.dispatchEvent(readyEvent); // Also dispatch on document for compatibility
        window.mainAppReady = true;
        this.attemptHideCriticalLoader();
    }

    // Handle initialization error
    handleInitError(error) {
        handleInitError(error, debugMode === '1' ? '1' : '0');
    }
    
    // Get module instance
    getModule(name) {
        return this.modules[name];
    }
    
    // Configuration management
    getConfig() {
        return this.configManager.getConfig();
    }

    updateConfig(newConfig) {
        this.configManager.updateConfig(newConfig);
        this.config = this.configManager.config;
        this.configManager.applyConfigChanges(this.modules);
    }
}

// Export for global access is handled by main.js entrypoint
window.MainApp = MainApp; // Export class constructor too
export default MainApp; // Export the class, not the instance 